import React, { useState } from "react";
import { Box, Typography } from "@mui/material";
import { Add } from "@mui/icons-material";
import {
  getDaysInMonth,
  getFirstDayOfMonth,
  isPastDate,
  isTodayDate,
} from "../../calenderUtils";

const CalendarGrid = ({ date, onAddDate, regularizations, statuses }) => {
  const [hoveredDate, setHoveredDate] = useState(null);

  const dayNames = ["SUN", "MON", "TUE", "WED", "THU", "FRI", "SAT"];

  const renderCalendar = () => {
    const daysInMonth = getDaysInMonth(date);
    const firstDay = getFirstDayOfMonth(date);
    const days = [];

    const statusMap = statuses?.reduce((acc, item) => {
      acc[item.date] = item.status;
      return acc;
    }, {});

    const prevMonth = new Date(date.getFullYear(), date.getMonth(), 0);
    const prevMonthDays = prevMonth.getDate();

    for (let i = firstDay - 1; i >= 0; i--) {
      days.push(
        <Box
          key={`prev-${prevMonthDays - i}`}
          sx={{
            width: 40,
            height: 40,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            color: "#ccc",
            fontSize: "14px",
            cursor: "default",
          }}
        >
          {prevMonthDays - i}
        </Box>
      );
    }

    // Current month days
    for (let day = 1; day <= daysInMonth; day++) {
      // Alternative approach: Create date string manually to avoid timezone issues
      const year = date.getFullYear();
      const month = String(date.getMonth() + 1).padStart(2, "0"); // Convert back to 1-indexed for string
      const dayStr = String(day).padStart(2, "0");
      const dateStr = `${year}-${month}-${dayStr}`;

      const status = statusMap[dateStr];

      const isToday = isTodayDate(date.getFullYear(), date.getMonth(), day);
      const isPast = isPastDate(date.getFullYear(), date.getMonth(), day);

      const showAddIcon = status === 202;
      const hasRegularization = regularizations.some((reg) => reg.date === day);

      const backgroundColor = isToday
        ? "#4CAF50"
        : hasRegularization
        ? "#E3F2FD"
        : "transparent";

      const color = isToday ? "white" : status === 202 ? "#333" : "#9CA3AF";
      const borderRadius = isToday ? "50%" : "0";

      days.push(
        <Box
          key={day}
          onMouseEnter={() => setHoveredDate(day)}
          onMouseLeave={() => setHoveredDate(null)}
          sx={{
            width: 30,
            height: 30,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            fontSize: "14px",
            cursor: status === 202 ? "default" : "pointer",
            position: "relative",
            backgroundColor,
            border: hasRegularization && "1px solid #1976D1",
            color,
            borderRadius,
            fontWeight: isToday ? "bold" : "normal",
            "&:hover": {
              backgroundColor: isToday ? "#4CAF50" : "#f0f0f0",
            },
          }}
        >
          {day}
          {hoveredDate === day && showAddIcon && isPast && (
            <Box
              sx={{
                position: "absolute",
                top: -5,
                right: -5,
                width: 20,
                height: 20,
                backgroundColor: "",
                borderRadius: "50%",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                cursor: "pointer",
                border: "1px solid #1976D2",
              }}
            >
              <Add
                onClick={() => onAddDate(day)}
                sx={{ fontSize: 16, color: "#1976D2", marginTop: "1px" }}
              />
            </Box>
          )}
        </Box>
      );
    }

    // Next month days
    const totalCells = Math.ceil((firstDay + daysInMonth) / 7) * 7;
    const nextMonthDays = totalCells - (firstDay + daysInMonth);

    for (let day = 1; day <= nextMonthDays; day++) {
      days.push(
        <Box
          key={`next-${day}`}
          sx={{
            width: 40,
            height: 40,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            color: "#ccc",
            fontSize: "14px",
            cursor: "default",
          }}
        >
          {day}
        </Box>
      );
    }

    return days;
  };

  return (
    <Box sx={{ mb: 3 }}>
      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: "repeat(7, 1fr)",
          gap: 1,
          mb: 1,
        }}
      >
        {dayNames.map((day) => (
          <Typography
            key={day}
            sx={{
              textAlign: "center",
              fontWeight: "bold",
              fontSize: "12px",
              color: "#666",
              p: 1,
            }}
          >
            {day}
          </Typography>
        ))}
      </Box>

      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: "repeat(7, 1fr)",
          gap: 1,
        }}
      >
        {renderCalendar()}
      </Box>
    </Box>
  );
};

export default CalendarGrid;
